"use client";

import React, { useEffect, useState } from "react";
import Script from "next/script";
import { HiSpeakerWave, HiSpeakerXMark } from "react-icons/hi2";

const DEFAULT_CONFIG = {
  registrationOpen: true,
};

export default function HeroSection({ onRegister }) {
  const [isMuted, setIsMuted] = useState(true);
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

    const fetchSeminarConfig = async () => {
      try {
        const response = await fetch(
          `${baseUrl}/api/seminar-config/neet-ug-2026-summit`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            cache: "no-store",
          }
        );

        if (!response.ok) {
          return;
        }

        const json = await response.json();

        if (json?.data && typeof json.data === "object") {
          setCfg({
            ...DEFAULT_CONFIG,
            ...json.data,
          });
        }
      } catch (error) {
        console.warn("Seminar config not reachable. Using default config.");
      }
    };

    fetchSeminarConfig();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window._wq = window._wq || [];
    window._wq.push({
      id: "owq6ld0r7t",
      options: {
        autoPlay: true,
        muted: true,
        loop: true,
        playsinline: true,
        videoFoam: true,
      },
      onReady: function (video) {
        video.mute();
        video.play();

        video.bind("end", function () {
          video.time(0);
          video.play();
        });
      },
    });
  }, []);

  const handleRegisterClick = () => {
    if (cfg.registrationOpen === false) {
      return;
    }

    if (typeof onRegister === "function") {
      onRegister();
    }
  };

  const handleToggleMute = () => {
    if (typeof window === "undefined" || !window.Wistia) return;

    const video = window.Wistia.api("owq6ld0r7t");
    if (!video) return;

    if (isMuted) {
      video.unmute();
      video.volume(1);
      video.play();
      setIsMuted(false);
    } else {
      video.mute();
      setIsMuted(true);
    }
  };

  return (
    <section className="relative left-1/2 flex min-h-[55vh] w-screen -translate-x-1/2 items-center overflow-hidden bg-[#fffaf6]">
      <Script
        src="https://fast.wistia.com/assets/external/E-v1.js"
        strategy="afterInteractive"
      />

      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-14">
        <div className="grid min-w-0 items-center gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-10 xl:gap-12">
          {/* LEFT CONTENT */}
          <div className="order-2 flex min-w-0 items-center lg:order-1">
            <div className="max-w-xl">
              <span className="inline-flex items-center rounded-full border border-[#ffd7bd] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#ff9245] shadow-sm">
                NEET UG 2026 Seminar
              </span>

              <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-4xl lg:text-4xl xl:text-5xl">
                India’s Biggest NEET UG 2026 Admission{" "}
                <span className="text-[#ff9245]">Seminar</span>
              </h2>

              <p className="mt-6 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
                Join our expert-led seminar and learn how to make the right
                decisions during NEET counselling.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleRegisterClick}
                  disabled={cfg.registrationOpen === false}
                  className={`rounded-full px-10 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition duration-300 ${
                    cfg.registrationOpen !== false
                      ? "bg-[#ff9245] hover:-translate-y-0.5 hover:bg-[#f07f2d] cursor-pointer"
                      : "bg-zinc-400 cursor-not-allowed opacity-60"
                  }`}
                >
                  {cfg.registrationOpen !== false
                    ? "Reserve Your Seat"
                    : "Registrations Closed"}
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT VIDEO */}
          <div className="order-1 min-w-0 lg:order-2">
            <div className="relative mx-auto mt-16 w-full max-w-[680px] overflow-hidden rounded-2xl shadow-xl sm:mt-16 lg:mt-0 lg:max-w-[520px] xl:max-w-none">
              <div className="aspect-video w-full">
                <div className="wistia_embed wistia_async_owq6ld0r7t videoFoam=true autoPlay=true muted=true silentAutoPlay=true playbar=true controlsVisibleOnLoad=true fullscreenButton=true h-full w-full" />
              </div>

              <button
                type="button"
                onClick={handleToggleMute}
                className="absolute right-3 top-3 z-20 flex items-center justify-center rounded-full bg-black/75 p-2 text-white shadow-md transition hover:bg-black hover:scale-105"
              >
                {isMuted ? (
                  <HiSpeakerXMark size={24} />
                ) : (
                  <HiSpeakerWave size={24} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}